import axios from 'axios'

const envUrl = (import.meta as any).env?.VITE_API_URL
const isBrowser = typeof window !== 'undefined'
const defaultUrl = isBrowser
  ? ((window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') ? 'http://localhost:3001/api' : '/api')
  : 'http://localhost:3001/api'
const API_BASE_URL = envUrl || defaultUrl

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status
    const url = String(error?.config?.url || '')
    const originalRequest = error.config || {}
    if (status === 401) {
      const isAuthPath = url.startsWith('/auth/login') || url.startsWith('/auth/register') || url.startsWith('/auth/refresh')
      if (!isAuthPath) {
        try {
          const rt = localStorage.getItem('refreshToken')
          if (!rt) throw new Error('NO_REFRESH_TOKEN')
          const r = await api.post('/auth/refresh', { refreshToken: rt })
          const newToken = r.data?.token
          if (!newToken) throw new Error('刷新失败')
          localStorage.setItem('token', newToken)
          originalRequest.headers = {
            ...(originalRequest.headers || {}),
            Authorization: `Bearer ${newToken}`
          }
          return api.request(originalRequest)
        } catch (e) {
          const hasRt = !!localStorage.getItem('refreshToken')
          if (hasRt) {
            try {
              localStorage.removeItem('token')
              localStorage.removeItem('refreshToken')
              localStorage.removeItem('user')
            } catch {}
            if (typeof window !== 'undefined') window.location.href = '/'
          }
        }
      }
    }
    const msg = error?.response?.data?.error || error?.message || '请求失败'
    return Promise.reject(new Error(msg))
  }
)

export const login = async (email: string, password: string) => {
  return api.post('/auth/login', { email, password })
}

export const register = async (email: string, password: string, companyName: string) => {
  return api.post('/auth/register', { email, password, companyName })
}

export const createTransfer = async (transferData: {
  recipient: string
  amount: number
  description: string
  transferMode: 'REAL_TIME' | 'USER_CONFIRM'
  mchId?: string
  sceneId?: '1000' | '1011' | '1002' | '1013' | '1004' | '1005' | '1009' | '1010'
  recipientName?: string
}) => {
  return api.post('/transfer/create', transferData)
}

export const getTransferStatus = async (id: string) => {
  return api.get(`/transfer/status/${id}`)
}

export const getTransferHistory = async (params?: {
  startDate?: string
  endDate?: string
  status?: string
  page?: number
  limit?: number
}) => {
  return api.get('/transfer/history', { params })
}

export const getTransferPackage = async (transferNo: string) => {
  return api.get(`/transfer/package/${transferNo}`)
}

export const retryTransfer = async (payload: { id?: string; transferNo?: string }) => {
  return api.post('/transfer/retry', payload)
}

export const getApiSettings = async () => {
  return api.get('/settings/api')
}

export const updateApiSettings = async (settings: any) => {
  return api.put('/settings/api', settings)
}

export const updateApiKey = async (apiKey: string) => {
  return api.post('/settings/api/key', { apiKey })
}

export const verifyCert = async () => {
  return api.post('/settings/verify-cert')
}

export const uploadCertPem = async (certPem: string) => {
  return api.post('/settings/api/upload-cert', { certPem })
}

export const uploadKeyPem = async (keyPem: string) => {
  return api.post('/settings/api/upload-key', { keyPem })
}

export const getSummary = async () => {
  return api.get('/metrics/summary')
}

export const getMeMerchants = async () => {
  return api.get('/me/merchants')
}

export const switchMerchant = async (merchantId: string | number) => {
  return api.post('/auth/switch-merchant', { merchantId })
}

export const getBindings = async (params?: { phone?: string; appid?: string; channel?: string; page?: number; limit?: number; days?: number; order?: 'asc' | 'desc' }) => {
  return api.get('/bindings', { params })
}

export const createMpOauthLink = async (payload: { phone?: string; appid: string; scope?: 'snsapi_base' | 'snsapi_userinfo'; callbackBase?: string }) => {
  return api.post('/bind/wechat/mp/oauth-link', payload)
}

export const completeMpBind = async (payload: { ticket: string; phone: string; displayName?: string }) => {
  return api.post('/bind/wechat/mp/complete', payload)
}

export const miniCode2Session = async (payload: { appid: string; code: string; phone?: string; displayName?: string }) => {
  return api.post('/wechat/mini/code2session', payload)
}

export const appOauthExchange = async (payload: { appid: string; code: string; phone?: string; displayName?: string }) => {
  return api.post('/wechat/app/oauth', payload)
}

export const getAppids = async () => {
  return api.get('/appids')
}

export const addAppid = async (payload: { appid: string; name?: string; channel: 'mp' | 'mini' | 'app' | 'h5'; secret?: string }) => {
  return api.post('/appids', payload)
}

export const deleteAppid = async (id: string) => {
  return api.delete(`/appids/${id}`)
}

export const updateAppid = async (id: string, payload: { appid: string; name?: string; channel: 'mp' | 'mini' | 'app' | 'h5'; secret?: string }) => {
  return api.put(`/appids/${id}`, payload)
}

export default api
 
export const getUsers = async (params?: { email?: string; role?: 'admin' | 'operator' | 'auditor'; status?: 'active' | 'disabled'; page?: number; limit?: number; merchant_id?: string | number }) => {
  return api.get('/users', { params })
}

export const createUser = async (payload: { email: string; password: string; role: 'admin' | 'operator' | 'auditor'; companyName: string; merchant_id?: string | number }) => {
  return api.post('/users', payload)
}

export const updateUser = async (id: string, payload: { email?: string; password?: string; role?: 'admin' | 'operator' | 'auditor'; companyName?: string; status?: 'active' | 'disabled'; merchant_id?: string | number }) => {
  return api.put(`/users/${id}`, payload)
}

export const deleteUser = async (id: string) => {
  return api.delete(`/users/${id}`)
}

export const updateUserStatus = async (id: string, status: 'active' | 'disabled') => {
  return api.patch(`/users/${id}/status`, { status })
}

export const updateMe = async (payload: { email?: string; password?: string; companyName?: string }) => {
  return api.put('/users/me', payload)
}

export const getAllMerchants = async (params?: { name?: string; status?: string; page?: number; limit?: number }) => {
  return api.get('/merchants', { params })
}

export const createMerchant = async (payload: { name: string; status?: string }) => {
  return api.post('/merchants', payload)
}

export const updateMerchant = async (id: string | number, payload: { name?: string; status?: string }) => {
  return api.put(`/merchants/${id}`, payload)
}

export const deleteMerchant = async (id: string | number) => {
  return api.delete(`/merchants/${id}`)
}

export const getScenes = async () => {
  return api.get('/scenes')
}

export const addScene = async (payload: { sceneId: '1000' | '1011' | '1002' | '1013' | '1004' | '1005' | '1009' | '1010'; name?: string; status?: 'active' | 'disabled'; userRecvPerception?: string; reportInfos?: Array<{ info_type: string; info_content: string }> }) => {
  return api.post('/scenes', payload)
}

export const updateScene = async (id: string | number, payload: { name?: string; status?: 'active' | 'disabled'; userRecvPerception?: string; reportInfos?: Array<{ info_type: string; info_content: string }> }) => {
  return api.put(`/scenes/${id}`, payload)
}

export const deleteScene = async (id: string | number) => {
  return api.delete(`/scenes/${id}`)
}
